import React, { useState, useEffect, useCallback } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import BusMascot from './BusMascot';
import API_URL, { authFetch } from '../config/api';
import { hasToken } from '../auth/session';

/**
 * AlertPopup - Shows page alerts in the portal UI
 * 
 * IMPORTANT: Internal pages do NOT show here - they only appear on Raspberry Pi display
 * This popup only shows:
 * - 'drivers' pages to employees (driver role)
 * - 'all' pages to everyone
 */
const AlertPopup = () => {
  const { user } = useAuth();
  const [activePages, setActivePages] = useState([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [dismissed, setDismissed] = useState([]);
  const [isFlashing, setIsFlashing] = useState(true);

  // Determine if user is a driver (employee role, not admin/manager)
  const isDriver = user?.role === 'employee';
  const isOfficeStaff = user?.role === 'admin' || user?.role === 'manager';

  // Fetch active pages
  const fetchActivePages = useCallback(async () => {
    try {
      if (!hasToken()) return;

      const response = await authFetch(`${API_URL}/api/message-board/pages`);

      if (response.ok) {
        const data = await response.json();
        // Filter for active, unacknowledged pages that haven't been dismissed
        let active = (data.pages || []).filter(
          p => p.is_active && !p.is_acknowledged && !dismissed.includes(p.id)
        );

        // IMPORTANT: Internal pages go to Raspberry Pi ONLY, not to portal UI
        // This popup only shows:
        // - 'drivers' pages to drivers (employee role)
        // - 'all' pages to everyone
        
        if (isDriver) {
          // Drivers see 'drivers' or 'all' pages
          active = active.filter(p => p.audience === 'drivers' || p.audience === 'all');
        } else if (isOfficeStaff) {
          // Office staff (admin/manager) only see 'all' pages in popup
          // 'internal' pages go to Raspberry Pi display, not here
          active = active.filter(p => p.audience === 'all');
        } else {
          // Fallback: only 'all' pages
          active = active.filter(p => p.audience === 'all');
        }

        setActivePages(active);
      }
    } catch (error) {
      console.error('Error fetching pages:', error);
    }
  }, [dismissed, isDriver, isOfficeStaff]);

  // Poll for active pages every 5 seconds
  useEffect(() => {
    fetchActivePages();
    const interval = setInterval(fetchActivePages, 5000);
    return () => clearInterval(interval);
  }, [fetchActivePages]);

  // Cycle through pages if multiple
  useEffect(() => {
    if (activePages.length > 1) {
      const interval = setInterval(() => {
        setCurrentPageIndex(prev => (prev + 1) % activePages.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [activePages.length]);

  // Flash animation toggle
  useEffect(() => {
    if (activePages.length > 0) {
      const flashInterval = setInterval(() => {
        setIsFlashing(prev => !prev);
      }, 500);
      return () => clearInterval(flashInterval);
    }
  }, [activePages.length]);

  const handleDismiss = (pageId) => {
    setDismissed(prev => [...prev, pageId]);
  };

  const handleAcknowledge = async (pageId) => {
    try {
      await authFetch(`${API_URL}/api/message-board/pages/${pageId}/acknowledge`, { method: 'PUT' });
      setDismissed(prev => [...prev, pageId]);
      fetchActivePages();
    } catch (error) {
      console.error('Error acknowledging page:', error);
    }
  };

  // Don't render if no active pages
  if (activePages.length === 0) return null;

  const currentPage = activePages[currentPageIndex] || activePages[0];
  if (!currentPage) return null;

  const isUrgent = currentPage.priority === 'urgent' || currentPage.priority === 'high';
  const isDriverAlert = currentPage.audience === 'drivers';

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: isFlashing && isUrgent
        ? 'rgba(220, 38, 38, 0.95)'
        : isDriverAlert
          ? 'rgba(37, 99, 235, 0.95)'
          : 'rgba(0, 0, 0, 0.7)',
      animation: isUrgent ? 'pulse 0.5s ease-in-out infinite' : 'none',
      transition: 'background 0.3s ease'
    }}>
      {/* Alert Card */}
      <div style={{
        background: '#1E1E1E',
        borderRadius: 16,
        padding: 0,
        maxWidth: 500,
        width: '90%',
        boxShadow: '0 25px 50px rgba(0,0,0,0.7)',
        overflow: 'hidden',
        animation: 'bounceIn 0.5s ease-out',
        border: '1px solid #2A2A2A'
      }}>
        {/* Header */}
        <div style={{
          background: isUrgent
            ? 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)'
            : isDriverAlert
              ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'
              : 'linear-gradient(135deg, #D4A94E 0%, #C49A3E 100%)',
          padding: '20px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 32 }}>
              {isDriverAlert ? '🚌' : isUrgent ? '🚨' : '📟'}
            </span>
            <div>
              <div style={{ 
                color: 'white', 
                fontSize: 24, 
                fontWeight: 700,
                textShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}>
                {isDriverAlert ? 'DRIVER ALERT' : isUrgent ? 'URGENT PAGE' : 'ATTENTION'}
              </div>
              <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: 14 }}>
                {activePages.length > 1 && `Alert ${currentPageIndex + 1} of ${activePages.length}`}
              </div>
            </div>
          </div>
          <button
            onClick={() => handleDismiss(currentPage.id)}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: 8,
              padding: 8,
              cursor: 'pointer',
              color: 'white',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
          >
            <XMarkIcon style={{ width: 24, height: 24 }} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px 24px 16px', textAlign: 'center' }}>
          {/* Bus Mascot */}
          <div style={{ 
            margin: '0 auto 20px',
            animation: 'bounce 1s ease-in-out infinite'
          }}>
            <BusMascot size={120} animated={true} showBubble={false} />
          </div>

          {/* Employee Name */}
          <div style={{
            fontSize: 32,
            fontWeight: 700,
            color: '#FFFFFF',
            marginBottom: 12,
            animation: isUrgent ? 'pulse 1s ease-in-out infinite' : 'none'
          }}>
            {currentPage.employee_name}
          </div>

          {/* Message */}
          <div style={{
            fontSize: 18,
            color: '#E0E0E0',
            marginBottom: 24,
            padding: '16px 20px',
            background: '#141414',
            borderRadius: 12,
            border: '2px dashed #2A2A2A'
          }}>
            {currentPage.message}
          </div>

          {/* Badges */}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
            {/* Audience Badge */}
            <div style={{
              display: 'inline-block',
              padding: '6px 16px',
              borderRadius: 20,
              fontSize: 12,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 1,
              background: isDriverAlert ? 'rgba(37,99,235,0.1)' : 'rgba(34,197,94,0.1)',
              color: isDriverAlert ? '#60a5fa' : '#4ADE80',
              border: `2px solid ${isDriverAlert ? 'rgba(37,99,235,0.3)' : 'rgba(34,197,94,0.3)'}`
            }}>
              {isDriverAlert ? '🚌 Driver Alert' : '👥 Everyone'}
            </div>

            {/* Priority Badge */}
            <div style={{
              display: 'inline-block',
              padding: '6px 16px',
              borderRadius: 20,
              fontSize: 12,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 1,
              background: isUrgent ? 'rgba(239,68,68,0.1)' : 'rgba(212,169,78,0.1)',
              color: isUrgent ? '#F87171' : '#D4A94E',
              border: `2px solid ${isUrgent ? 'rgba(239,68,68,0.3)' : 'rgba(212,169,78,0.3)'}`
            }}>
              {currentPage.priority} Priority
            </div>
          </div>
        </div>

        {/* Actions */}
        <div style={{
          padding: '16px 24px 24px',
          display: 'flex',
          gap: 12,
          justifyContent: 'center'
        }}>
          <button
            onClick={() => handleDismiss(currentPage.id)}
            style={{
              padding: '12px 24px',
              fontSize: 16,
              fontWeight: 600,
              background: '#141414',
              color: '#E0E0E0',
              border: '2px solid #2A2A2A',
              borderRadius: 8,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#2A2A2A';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#141414';
            }}
          >
            Dismiss
          </button>
          <button
            onClick={() => handleAcknowledge(currentPage.id)}
            style={{
              padding: '12px 32px',
              fontSize: 16,
              fontWeight: 600,
              background: isUrgent
                ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
                : isDriverAlert
                  ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'
                  : 'linear-gradient(135deg, #D4A94E 0%, #C49A3E 100%)',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
            }}
          >
            ✓ Acknowledge
          </button>
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes bounceIn {
          0% {
            opacity: 0;
            transform: scale(0.3);
          }
          50% {
            transform: scale(1.05);
          }
          70% {
            transform: scale(0.9);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.8;
          }
        }
      `}</style>
    </div>
  );
};

export default AlertPopup;
